import { mapProductPairingListItem, type ProductPairingListItemApi, type ProductPairingRow } from "@app/views/warehouse/productPairing/config/data";
import { toProductPairingExportRequestParams, toProductPairingListRequestParams, type ProductPairingQueryParam } from "@app/views/warehouse/productPairing/config/filter";
import {
    mapProductPairingCandidateItem,
    type ProductPairingCandidateItemApi,
    type ProductPairingCandidateRow,
    type ProductPairingDialogQueryParam,
} from "@app/views/warehouse/productPairing/config/pairing-dialog.data";
import { mapProductPairingLogItem, type ProductPairingLogItemApi, type ProductPairingLogRow } from "@app/views/warehouse/productPairing/config/pairing-log.data";
import type { FetchListOptions, ListResult } from "@shared/types/list";
import type { BatchResultResponse } from "@shared/utils/batchResult";
import { unwrapDownloadBlob } from "@shared/utils/download";
import { resolveCombinedSearchForRequest } from "@shared/utils/filterQuery";
import { http } from "@shared/utils/request";

function toRequestStringArray(value: string | string[] | undefined): string[] | undefined {
    if (value === undefined) return undefined;
    if (Array.isArray(value)) {
        const list = value.map(item => String(item).trim()).filter(Boolean);
        return list.length ? list : undefined;
    }
    const text = String(value).trim();
    return text ? [text] : undefined;
}

/** 产品配对列表：POST /manage/warehouseProductMatch/list */
export async function fetchProductPairingList(query: ProductPairingQueryParam, options?: FetchListOptions): Promise<ListResult<ProductPairingRow>> {
    const data = await http.post<{ items: ProductPairingListItemApi[]; total: number }>("/manage/warehouseProductMatch/list", toProductPairingListRequestParams(query), { signal: options?.signal });

    return {
        list: (data.items ?? []).map(mapProductPairingListItem),
        total: data.total ?? 0,
    };
}

/** 异步导出产品配对，进入下载中心 */
export function exportProductPairingList(params: ProductPairingQueryParam) {
    return http.post<{ taskId: string; downloadTaskId: string }>("/manage/warehouseProductMatch/export", toProductPairingExportRequestParams(params));
}

/** 下载产品配对导入模板，直接返回文件流 */
export async function downloadProductPairingImportTemplate() {
    const blob = await http.post<Blob>("/manage/warehouseProductMatch/importTemplate", {}, { responseType: "blob" });
    return unwrapDownloadBlob(blob);
}

/** 批量导入产品配对：POST /manage/warehouseProductMatch/import */
export function importProductPairing(file: File) {
    const formData = new FormData();
    formData.append("file", file);
    return http.post<BatchResultResponse>("/manage/warehouseProductMatch/import", formData);
}

/**
 * 配对候选列表：
 * POST /manage/warehouseProductMatch/candidateList
 */
export async function fetchProductPairingCandidateList(query: ProductPairingDialogQueryParam, options?: FetchListOptions): Promise<ListResult<ProductPairingCandidateRow>> {
    const body: Record<string, unknown> = {
        page: query.page,
        perPage: query.limit,
        warehouseSkuId: query.warehouseSkuId,
    };

    if (query.type !== "" && query.type != null) {
        body.typeIn = [Number(query.type)];
    }
    if (query.ownerUserId) {
        body.ownerUserIdIn = [String(query.ownerUserId)];
    }

    const asin = toRequestStringArray(resolveCombinedSearchForRequest(query.asin as unknown as string | string[]));
    if (asin !== undefined) body.asin = asin;

    const sku1p = toRequestStringArray(resolveCombinedSearchForRequest(query.sku1p as unknown as string | string[]));
    if (sku1p !== undefined) body.sku1p = sku1p;

    const data = await http.post<{ items: ProductPairingCandidateItemApi[]; total: number }>("/manage/warehouseProductMatch/candidateList", body, {
        signal: options?.signal,
    });

    return {
        list: (data.items ?? []).map(mapProductPairingCandidateItem),
        total: data.total ?? 0,
    };
}

export interface ProductPairingBindItem {
    productId: string;
    warehouseSkuId: string;
    matchQty: number;
}

/** 配对：POST /manage/warehouseProductMatch/bind */
export function bindProductPairing(items: ProductPairingBindItem[]) {
    return http.post<BatchResultResponse>("/manage/warehouseProductMatch/bind", { items });
}

export interface ProductPairingRebindPayload {
    matchId: string;
    productId: string;
    matchQty: number;
}

/** 更换配对：POST /manage/warehouseProductMatch/rebind */
export function rebindProductPairing(payload: ProductPairingRebindPayload) {
    return http.post("/manage/warehouseProductMatch/rebind", payload);
}

/** 解除配对：POST /manage/warehouseProductMatch/unbind */
export function unbindProductPairing(matchIds: string[]) {
    return http.post<BatchResultResponse>("/manage/warehouseProductMatch/unbind", { matchIds });
}

/** 配对日志：POST /manage/warehouseProductMatch/logList */
export async function fetchProductPairingLogList(params: { warehouseSkuId: string; matchIds?: string[] }): Promise<ProductPairingLogRow[]> {
    const body: Record<string, unknown> = {
        warehouseSkuId: params.warehouseSkuId,
    };
    if (params.matchIds?.length) body.matchIds = params.matchIds.map(String);
    const data = await http.post<{ items: ProductPairingLogItemApi[] }>("/manage/warehouseProductMatch/logList", body);
    return (data.items ?? []).map(mapProductPairingLogItem);
}

export interface ExternalWarehouseOptionsParams {
    /** 三方仓 ID */
    providerAccountId?: string;
    /** 状态（1-启用，2-禁用） */
    status?: Array<number | string>;
    /** 仓库名称 */
    warehouseName?: string[];
    /** 企业 ID */
    corpId?: string;
    /** VC 类型（PO / DF） */
    vcType?: "PO" | "DF";
    /** 客户订单明细 ID（手动生成发货单：按明细取可用三方仓 SKU） */
    customerOrderItemId?: string;
    /** 仓库代码（DF 手动生成发货单 / 重新推单：取详情 shipFromPartyId） */
    warehouseCode?: string;
    vcWarehouseCode?: string;
}

interface ExternalWarehouseOptionApiItem {
    id?: string;
    warehouseCode?: string;
    warehouseName?: string;
    systemWarehouseName?: string;
    /** 三方仓 SKU（接口按明细维度返回时才有） */
    warehouseSku?: string;
    /** 三方仓 SKU ID（提交时作为 deliveryWarehouseSkuId） */
    warehouseSkuId?: string;
}

async function fetchExternalWarehouseOptionItems(params: ExternalWarehouseOptionsParams) {
    const data = await http.post<{ items: ExternalWarehouseOptionApiItem[] }>("/manage/warehouse/external/options", params);
    return data.items ?? [];
}

function toSelectOptions(items: Array<{ label: string; value: string | number | null | undefined }>) {
    return items.map(item => (item.value == null || !item.label ? null : { label: item.label, value: String(item.value) })).filter((item): item is { label: string; value: string } => Boolean(item));
}

/**
 * 1P仓库名称下拉：
 * POST /manage/warehouse/external/options
 */
export async function fetchExternalWarehouseSelectOptions(params: ExternalWarehouseOptionsParams = {}) {
    const items = await fetchExternalWarehouseOptionItems(params);
    return toSelectOptions(items.map(item => ({ label: item.systemWarehouseName || item.warehouseName || "", value: item.id })));
}

/**
 * 三方仓SKU下拉（按企业 + 1PSKU + 海外仓取可选 SKU）：
 * POST /manage/warehouseProductMatch/options
 * label = warehouseSku，value = warehouseSkuId（提交作 deliveryWarehouseSkuId）
 */
export async function fetchWarehouseProductMatchOptions(params: { corpId: string; sku1p: string[]; externalWarehouseId: string }) {
    const data = await http.post<{
        items: Array<{
            warehouseSkuId?: string;
            warehouseSku?: string;
            productNameSnapshot?: string;
        }>;
    }>("/manage/warehouseProductMatch/options", {
        corpId: params.corpId,
        sku1p: params.sku1p,
        externalWarehouseId: params.externalWarehouseId,
    });
    return toSelectOptions(
        (data.items ?? []).map(item => ({
            label: item.warehouseSku || item.productNameSnapshot || "",
            value: item.warehouseSkuId,
        })),
    );
}
